import { useMsal } from '@azure/msal-react';
import { Button, Grid } from '@material-ui/core';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { BrandProps, ClientConfig } from '../../../../clientConfig';
import { PromotionState } from '../../../../types/reactTypes';
import { ClientConfigContext } from '../../../clientConfigContext';
import useDeviceWidth from '../../../hooks/useDeviceWidth';
import { useValidatedUrl } from '../../../hooks/useValidatedUrl';
import { AppPromotionEvents, buildAppPromotionEventProps, sendEvent } from '../../../services/amplitudeClient';
import getAccessToken from '../../../util/getAccessToken';
import { savePromotionStateAndRedirect } from '../../../util/promotionUtils';
import { redirectWithSoftLogout } from '../../../util/sessionTimeout';
import TwoButtonContainer from '../../containers/TwoButtonContainer';
import withLogger, { ComponentLoggerProps } from '../../HOC/WithLogger';

export const AppPromotionButtonsComponent = ({ brandInfo, log }: BrandProps & ComponentLoggerProps) => {
    const location = useLocation<PromotionState>();
    const clientConfiguration = useContext<ClientConfig>(ClientConfigContext);
    const validateRedirectUrl = useValidatedUrl();
    const cdnPath = clientConfiguration.cdnPath;
    const { t } = useTranslation();

    const { isSmallWidth } = useDeviceWidth();
    const { instance, accounts } = useMsal();

    // Desktop view App Store download button handler
    const redirectToAppStore = () => {
        sendAppPromotionEvent(AppPromotionEvents.DOWNLOAD_APP_FROM_PROMOTION);
        savePromotionStateAndRedirect(brandInfo.promotion?.appStoreRedirectLink as string);
    };

    // Desktop view Play Store download button handler
    const redirectToPlayStore = () => {
        sendAppPromotionEvent(AppPromotionEvents.DOWNLOAD_APP_FROM_PROMOTION);
        savePromotionStateAndRedirect(brandInfo.promotion?.playStoreRedirectLink as string);
    };

    // Mobile view SKIP button handler
    const handleRedirect = async () => {
        await sendAppPromotionEvent(AppPromotionEvents.SKIP_APP_PROMOTION);
        redirectWithSoftLogout(instance, location.state?.redirectUrl, validateRedirectUrl);
    };

    // Mobile view download app button handler
    const redirectToProperAppSource = () => {
        sendAppPromotionEvent(AppPromotionEvents.DOWNLOAD_APP_FROM_PROMOTION);
        savePromotionStateAndRedirect(brandInfo.promotion?.platformStoreRedirectLink as string);
    };

    const sendAppPromotionEvent = async (eventType: string) => {
        const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes, log);
        const eventProps = buildAppPromotionEventProps(eventType, brandInfo.name ?? brandInfo.brand);
        return sendEvent(eventProps, jwt);
    };

    if (isSmallWidth) {
        return (
            <Grid container item xs={12} className='promotionButtonContainer'>
                <TwoButtonContainer
                    firstButtonProps={{
                        variant: 'text',
                        label: t('Skip'),
                        onClick: handleRedirect,
                        brand: brandInfo.brand,
                    }}
                    secondButtonProps={{
                        variant: 'contained',
                        type: 'button',
                        onClick: redirectToProperAppSource,
                        label: t('Download App'),
                        brand: brandInfo.brand,
                    }}
                />
            </Grid>
        );
    } else {
        return (
            <>
                <Grid className='promotionWebButtons' container item xs={10} sm={10} direction='column'>
                    <Grid item xs={6} className='scannerImage'>
                        <img src={`${cdnPath}${brandInfo.promotion?.qrCodeImage}`} />
                    </Grid>
                    <Grid item xs={6}>
                        <Grid item>
                            <Button className='hoverHiddenBtn' onClick={redirectToAppStore}>
                                <img src={`${cdnPath}${brandInfo.promotion?.appStoreImage}`} />
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button className='hoverHiddenBtn' onClick={redirectToPlayStore}>
                                <img src={`${cdnPath}${brandInfo.promotion?.playStoreImage}`} />
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </>
        );
    }
};

export const AppPromotionButtons = withLogger(AppPromotionButtonsComponent, { eventSource: 'AppPromotionButtons' });
